import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Archive, Search, Loader2, RotateCcw, Trash2, Facebook, Globe, Mail, Checkbox,
} from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_CONFIG = {
  meta_lead: { label: 'Meta', icon: Facebook, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  wix_lead: { label: 'Website', icon: Globe, color: 'bg-teal-100 text-teal-700 border-teal-200' },
  email_lead: { label: 'Email', icon: Mail, color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

export const LeadsArchive = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (search) params.search = search;
      const [leadsRes, statsRes] = await Promise.all([
        api.get('/archived-leads', { params }),
        api.get('/archived-leads/stats'),
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data || {});
    } catch {
      toast.error('Failed to load archived leads');
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleRestore = async (id) => {
    try {
      const res = await api.post(`/archived-leads/${id}/restore`);
      toast.success(res.data.message);
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      fetchLeads();
    } catch {
      toast.error('Failed to restore lead');
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('Permanently delete this lead? This cannot be undone.')) return;
    try {
      await api.delete(`/archived-leads/${id}`);
      toast.success('Lead permanently deleted');
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      fetchLeads();
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    if (action === 'delete' && !window.confirm(`Permanently delete ${selected.size} lead(s)?`)) return;
    setActionLoading(true);
    try {
      const res = await api.post('/archived-leads/bulk-action', { lead_ids: [...selected], action });
      toast.success(res.data.message);
      setSelected(new Set());
      fetchLeads();
    } catch {
      toast.error(`Failed to ${action} leads`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === leads.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(leads.map((l) => l.id)));
    }
  };

  const SourceBadge = ({ source }) => {
    const cfg = SOURCE_CONFIG[source] || { label: source, color: 'bg-slate-100 text-slate-700' };
    const Icon = cfg.icon;
    return (
      <Badge className={`text-[11px] font-medium gap-1 ${cfg.color}`} data-testid={`source-badge-${source}`}>
        {Icon && <Icon className="w-3 h-3" />} {cfg.label}
      </Badge>
    );
  };

  const filters = [
    { key: 'all', label: 'All', count: stats.total || 0 },
    { key: 'meta_lead', label: 'Meta', count: stats.meta_lead || 0 },
    { key: 'wix_lead', label: 'Website', count: stats.wix_lead || 0 },
    { key: 'email_lead', label: 'Email', count: stats.email_lead || 0 },
  ];

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4" data-testid="leads-archive-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Archive className="w-5 h-5 text-slate-500" /> Leads Archive
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Deleted leads from all sources. Restore or permanently remove.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {filters.map((f) => (
            <Button
              key={f.key}
              variant={sourceFilter === f.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSourceFilter(f.key)}
              data-testid={`filter-${f.key}`}
            >
              {f.label} ({f.count})
            </Button>
          ))}
          <div className="flex-1" />
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search archived leads..."
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="archive-search-input"
            />
          </div>
        </div>

        {selected.size > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-3 flex items-center gap-3">
              <span className="text-sm font-medium text-blue-800">{selected.size} selected</span>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('restore')} disabled={actionLoading} data-testid="bulk-restore-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleBulkAction('delete')} disabled={actionLoading} data-testid="bulk-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Forever
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Archive className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="font-medium">Archive is empty</p>
                <p className="text-sm mt-1">Deleted leads from Meta, Website, and Email will appear here.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={selected.size === leads.length && leads.length > 0}
                        onChange={toggleAll}
                        className="rounded"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Original Date</TableHead>
                    <TableHead>Archived</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id} className={selected.has(lead.id) ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selected.has(lead.id)}
                          onChange={() => toggleSelect(lead.id)}
                          className="rounded"
                          data-testid={`select-${lead.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm text-slate-900 dark:text-white">
                          {lead.first_name} {lead.last_name}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600 dark:text-slate-300">{lead.email}</p>
                        {lead.phone && <p className="text-xs text-slate-400">{lead.phone}</p>}
                      </TableCell>
                      <TableCell>
                        <SourceBadge source={lead.source} />
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {lead.submitted_at ? formatDate(lead.submitted_at) : '-'}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-500">{formatDate(lead.archived_at)}</p>
                        <p className="text-xs text-slate-400">{lead.archived_by}</p>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => handleRestore(lead.id)}
                            title="Restore"
                            data-testid={`restore-${lead.id}`}
                          >
                            <RotateCcw className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => handlePermanentDelete(lead.id)}
                            title="Permanently delete"
                            data-testid={`perm-delete-${lead.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
